import _ from "lodash";
import ACTIONS from "../Design_Asset_Actions.json";

// Constants
const MODBUS_BROADCAST_ID = "255";
const MODBUS_MOD_DIVISOR = 7;
const DEFAULT_IP_ADDRESS_KEY = "ipAddress1";

// CB IFE IP Addresses
const CB_IFE_IP_ADDRESSES = {
  IFE_IP_1: "192.168.50.21",
  IFE_IP_2: "192.168.50.22",
  IFE_IP_3: "192.168.50.23",
  IFE_IP_4: "192.168.50.24",
};

// Default template for CB/Contactor configuration
const defaultContactorTemplate = {
  name: "",
  description: "",
  loadCategory: 0,
  loadPriority: 0,
  cbAssetDetails: [],
  cbCommunicationData: {},
};
const initialCBGTList = [
  {
    assetConnected: "GT",
    modbusSlaveId: "",
    configData: {
      name: "",
      description: "",
      maximum_available_power: 0,
    },
    CBId: "CB_75",
    uiData: {
      status: false,
      CBSelected: false,
      CBConfigured: false,
      configured: false,
      contactor: false,
      detailsBlock: false,
      selected: false,
    },
    cbConfigData: {
      name: "",
      description: "",
      motorized: true,
      contactor: false,
      loadCategory: 0,
      loadPriority: 0,
      cbAssetDetails: [],
      cbCommunicationData: {},
    },
    contactorConfigData: {
      name: "",
      description: "",
      loadCategory: 0,
      loadPriority: 0,
      cbAssetDetails: [],
      cbCommunicationData: {},
    },
    communicationData: {},
    assetDetails: [],
  },
];
const communicationDataSample = {
  ipaddress: "",
  subnetmask: "",
  defaultGateway: "",
  modbusSlaveId: "",
};

/**
 * Determines modbus ID based on load count
 * @param {number} totalLoads - Total number of loads
 * @param {string} defaultId - Default modbus ID
 * @returns {string} Modbus ID (broadcast or default)
 */
const getModbusId = (totalLoads, defaultId) => {
  return totalLoads % MODBUS_MOD_DIVISOR === 0 ? MODBUS_BROADCAST_ID : defaultId;
};
/**
 * Adds a Grounding Transformer with initial configuration
 * @param {Array} CBGTList - Current GT list
 * @param {Object} defaultPreFill - Default configuration values
 * @param {Object} configData - Configuration data templates
 * @param {Object} count - Count of loads, DERs, and GTs
 * @param {Object} defaultCbData - Default CB data
 * @param {Object} contactor - Contactor configuration
 * @param {Object} gt - GT asset details
 * @returns {Array} Updated GT list
 */
const addGT = (
  CBGTList,
  defaultPreFill,
  configData,
  count,
  defaultCbData,
  contactor,
  gt,
) => {
  if (!Array.isArray(CBGTList) || CBGTList.length === 0) {
    return CBGTList;
  }

  let newGtData = _.cloneDeep(CBGTList);
  const totalLoads = count.loadCount + count.derCount + count.gTCount;
  const modbusId = getModbusId(totalLoads, defaultPreFill["Circuit Breaker"].modbusId_CB_75);

  newGtData[0].modbusSlaveId = modbusId;
  newGtData[0].uiData.status = true;
  newGtData[0].uiData.configured = true;
  newGtData[0].uiData.cbRequired = true;
  newGtData[0].uiData.CBConfigured = false;
  newGtData[0].uiData.contactor = false;

  newGtData[0].cbConfigData = { ...configData.sampleCbConfigData };
  newGtData[0].cbConfigData.cbAssetDetails = [];
  newGtData[0].cbConfigData.name = `${defaultPreFill["Circuit Breaker"].name} ${defaultPreFill["Grounding_Transformer"].name}`;
  newGtData[0].cbConfigData.cbCommunicationData = {
    ipaddress: defaultPreFill["Circuit Breaker"][DEFAULT_IP_ADDRESS_KEY],
    subnetmask: defaultPreFill["Circuit Breaker"].subnetMask,
    defaultGateway: defaultPreFill["Circuit Breaker"].defaultGateway,
    modbusSlaveId: modbusId,
  };
  newGtData[0].configData = { ...configData.gTConfigData };
  newGtData[0].configData.name = defaultPreFill["Grounding_Transformer"].name;
  newGtData[0].contactorConfigData.name = "GT Contactor";
  newGtData[0].contactorConfigData.cbAssetDetails[0] = { ...contactor };
  newGtData[0].assetDetails[0] = { ...gt };
  newGtData[0].cbConfigData.cbAssetDetails = [{ ...defaultCbData }];
  newGtData[0].communicationData = { ...communicationDataSample };

  // Determine if hardwired/serial connection
  const isHardwiredConnection = gt?.assetInfo === "Hardwired" || gt?.assetInfo === "Serial";
  newGtData[0].communicationData.ipaddress = isHardwiredConnection ? "" : defaultPreFill["Grounding_Transformer"].ipAddress;
  newGtData[0].communicationData.subnetmask = isHardwiredConnection ? "" : defaultPreFill["Grounding_Transformer"].subnetMask;
  newGtData[0].communicationData.defaultGateway = isHardwiredConnection ? "" : defaultPreFill["Grounding_Transformer"].defaultGateway;
  newGtData[0].communicationData.modbusSlaveId = defaultPreFill["Grounding_Transformer"].modbusId ?? MODBUS_BROADCAST_ID;

  return newGtData;
};
/**
 * Marks a GT as selected
 * @param {Array} CBGTList - Current GT list
 * @param {number} index - Index of GT to select
 * @returns {Array} Updated GT list
 */
const gtSelect = (CBGTList, index) => {
  if (!Array.isArray(CBGTList)) return CBGTList || initialCBGTList;
  const newCBGTList = _.cloneDeep(CBGTList);
  if (index < 0 || index >= newCBGTList.length) return newCBGTList;

  const item = newCBGTList[index] || {};
  newCBGTList[index] = {
    ...item,
    uiData: {
      ...(item.uiData || {}),
      status: true,
      selected: true,
      CBSelected: true,
    },
  };
  return newCBGTList;
};
/**
 * Marks a GT as removed/inactive
 * @param {Array} CBGTList - Current GT list
 * @param {number} index - Index of GT to remove
 * @returns {Array} Updated GT list
 */
const gtRemove = (CBGTList, index) => {
  if (!Array.isArray(CBGTList)) return CBGTList || initialCBGTList;
  const newCBGTList = _.cloneDeep(CBGTList);
  if (index < 0 || index >= newCBGTList.length) return newCBGTList;

  const item = newCBGTList[index] || {};
  newCBGTList[index] = {
    ...item,
    uiData: { ...(item.uiData || {}), status: false },
  };
  return newCBGTList;
};
/**
 * Updates GT configuration
 * @param {Array} CBGTList - Current GT list
 * @param {number} selectedGt - Index of GT to update
 * @param {Object} configData - New configuration data
 * @returns {Array} Updated GT list
 */
const updateHandleGtConfig = (CBGTList, selectedGt, configData) => {
  if (!Array.isArray(CBGTList) || selectedGt < 0 || selectedGt >= CBGTList.length) {
    return CBGTList;
  }

  const cbDataList = _.cloneDeep(CBGTList);
  cbDataList[selectedGt].configData = configData;
  cbDataList[selectedGt].uiData.configured = false;

  return cbDataList;
};
/**
 * Updates GT Circuit Breaker configuration
 * @param {Array} CBGTList - Current GT list
 * @param {number} selectedGtCB - Index of GT to update
 * @param {Object} cbConfigData - New CB configuration data
 * @returns {Array} Updated GT list
 */
const updateHandleGtCbConfig = (CBGTList, selectedGtCB, cbConfigData) => {
  if (!Array.isArray(CBGTList) || selectedGtCB < 0 || selectedGtCB >= CBGTList.length) {
    return CBGTList;
  }

  const cbDataList = _.cloneDeep(CBGTList);
  cbDataList[selectedGtCB].cbConfigData = {
    ...cbDataList[selectedGtCB].cbConfigData,
    ...cbConfigData,
  };
  cbDataList[selectedGtCB].uiData.CBConfigured = false;

  // Disable contactor if CB is not required
  if (cbDataList[selectedGtCB].uiData.cbRequired === false) {
    cbDataList[selectedGtCB].uiData.contactor = false;
  }

  return cbDataList;
};
/**
 * Updates load contactor configuration
 * @param {Array} CBGTList - Current GT list
 * @param {number} index - Index of GT to update
 * @param {boolean} checked - Whether contactor is enabled
 * @param {Object} defaultContactor - Default contactor asset details
 * @returns {Array} Updated GT list
 */
const updateLoadContactorConfigData = (
  CBGTList,
  index,
  checked,
  defaultContactor
) => {
  if (!Array.isArray(CBGTList) || index < 0 || index >= CBGTList.length) {
    return CBGTList;
  }

  const newCBGTList = _.cloneDeep(CBGTList);
  newCBGTList[index].uiData.contactor = checked;

  // Initialize contactor config from template
  newCBGTList[index].contactorConfigData = _.cloneDeep(defaultContactorTemplate);

  if (checked) {
    newCBGTList[index].contactorConfigData.name = `Load_Contactor_${index + 1}`;
    newCBGTList[index].contactorConfigData.cbAssetDetails[0] = defaultContactor;
  }

  return newCBGTList;
}
/**
 * Updates GT configuration with communication data
 * @param {Array} CBGTList - Current GT list
 * @param {number} selectedGt - Index of GT to update
 * @param {Object} configData - New configuration data
 * @param {Object} defaultValues - Default values (unused, kept for API compatibility)
 * @returns {Array} Updated GT list
 */
const updateGtConfig = (CBGTList, selectedGt, configData, defaultValues) => {
  if (!Array.isArray(CBGTList) || selectedGt < 0 || selectedGt >= CBGTList.length) {
    return CBGTList;
  }

  const newCBGTList = _.cloneDeep(CBGTList);
  newCBGTList[selectedGt].uiData.CBConfigured = true;

  return newCBGTList;
};
const updateGt = (
  CBGTList,
  componentsData,
) => {
  const newGtData = [...CBGTList];
  const attributes = JSON.parse(componentsData?.attributes);
  try {
    newGtData[0].uiData = JSON.parse(componentsData.uiAttributes);
  } catch (error) {
    console.error('Error parsing JSON:', error);
  }
  newGtData[0].uiData.selected = false;
  newGtData[0].uiData.CBSelected = false;
 const uiAttributes = componentsData?.uiAttributes;
 if(uiAttributes && uiAttributes !== undefined){
  newGtData[0].uiData.contactor = JSON.parse(uiAttributes)?.contactor;
 }
      
  
  newGtData[0].configData.name = componentsData.componentName;
  newGtData[0].configData.description = attributes.description;
   newGtData[0].componentId = componentsData?.componentId ?? null;
  newGtData[0].uuid = componentsData?.uuid ?? null;
  newGtData[0].assetType = componentsData.componentType;
  // const newEvCom = { ...evCommunicationData };
  // if (componentsData.communications[0] !== undefined) {
  //   newEvCom.ipaddress = componentsData.communications[0].ip;
  //   newEvCom.subnetmask = componentsData.communications[0].subnetmask;
  //   newEvCom.defaultGateway = componentsData.communications[0].defaultGateway;
  //   newEvCom.modbusSlaveId = componentsData.communications[0].modbusSlaveId;
  //   setEvCommunicationData(newEvCom);
  // }
  // newGtData[0].communicationData = newEvCom;
  if (componentsData.asset !== undefined) {
    // newGtData[0].assetDetails.push(componentsData.asset);
     newGtData[0].assetDetails[0] = componentsData.asset;
  }
  return newGtData;
};
const updateGtCb = (CBGTList, componentsData) => {
  const newGtData = _.cloneDeep(CBGTList);
  const attributes = JSON.parse(componentsData.attributes);
  newGtData[0].cbConfigData.name = componentsData.componentName;
  newGtData[0].cbConfigData.description = attributes.description;
  newGtData[0].cbConfigData.motorized =
    parseInt(attributes.motorized) === 1 ? true : false;
  newGtData[0].cbConfigData.contactor =
    parseInt(attributes.contactor) === 1 ? true : false;
  newGtData[0].cbConfigData.loadCategory = parseInt(attributes.loadCategory);
  newGtData[0].cbConfigData.loadPriority = parseInt(attributes.loadPriority);
  newGtData[0].cbConfigData.cbAssetDetails[0] = componentsData.asset;
  newGtData[0].modbusSlaveId = componentsData.communications[0].modbusSlaveId;
  if (componentsData.communications.length) {
    newGtData[0].cbConfigData.cbCommunicationData.ipaddress =
      componentsData.communications[0].ip;
    newGtData[0].cbConfigData.cbCommunicationData.subnetmask =
      componentsData.communications[0].subnetmask;
    newGtData[0].cbConfigData.cbCommunicationData.defaultGateway =
      componentsData.communications[0].defaultGateway;
    newGtData[0].cbConfigData.cbCommunicationData.modbusSlaveId =
      componentsData.communications[0].modbusSlaveId;
  }
 
  return newGtData;
};
const selectGtCbAsset = (
  CBGTList,
  index,
  selectedAsset,
  cbCommunicationValues
) => {
  const assetInfo = selectedAsset?.assetInfo
  const gtData = _.cloneDeep(CBGTList);
  const gtDataPrevious = CBGTList[index].cbConfigData;
  gtData[index].cbConfigData.cbAssetDetails[0] = selectedAsset;
  gtData[index].uiData.CBConfigured = false;

  const cbCommunicationData = getCBifeValues(
    gtDataPrevious,
    selectedAsset,
    cbCommunicationValues
  );
  if(assetInfo=="Hardwired"){
    gtData[index].uiData.contactor = false;
  }
  if(assetInfo=="Serial"){
    gtData[index].cbConfigData.cbCommunicationData = {
      defaultGateway:"",
      ipaddress:"",
      modbusSlaveId:"",
      subnetmask:""
    }
  }else{
    gtData[index].cbConfigData.cbCommunicationData = cbCommunicationData;
  }
  
  return gtData;
};
const getdefaultConfigGT = (CBGTList, defaultPreFill, configData) => {
  if (!Array.isArray(CBGTList) || CBGTList.length === 0) {
    return CBGTList;
  }
  let newGtData = _.cloneDeep(CBGTList);
  return newGtData;
}
const getPrevGtData = (CBGTList, gtData) => {
  if (!Array.isArray(gtData) || gtData.length === 0) {
    return CBGTList;
  }
  let newGtData = _.cloneDeep(gtData);
 
  return newGtData;
}
const CBGTListReducer = (state = initialCBGTList, action) => {
  switch (action.type) {
    case ACTIONS.ASSIGN_DEFAULT_GT_VALUES:
      
      return getdefaultConfigGT(state, action.payload?.defaultPreFill, action.payload?.configData); 
    case ACTIONS.ADD_GT:
      return addGT(
        state,
        action.payload.defaultPreFill,
        action.payload.configData,
        action.payload.count,
        action.payload.defaultCbData,
        action.payload.contactor,
        action.payload.gt,
      );
    case ACTIONS.GT_SELECT:
      return gtSelect(state, action.payload.index);
    case ACTIONS.REMOVE_GT:
      return gtRemove(state, action.payload.index);
    case ACTIONS.UPDATE_HANDLE_GT_CONFIG:
      return updateHandleGtConfig(
        state,
        action.payload.selectedGt,
        action.payload.configData
      );
    case ACTIONS.UPDATE_HANDLE_GT_CB_CONFIG:
      return updateHandleGtCbConfig(
        state,
        action.payload.selectedGtCB,
        action.payload.cbConfigData
      );
    case ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA:
      return updateLoadContactorConfigData(
        state,
        action.payload.index,
        action.payload.checked,
        action?.payload?.defaultContactor
      );

    case ACTIONS.UPDATE_GT_CONFIG:
      return updateGtConfig(
        state,
        action.payload.selectedGt,
        action.payload.configData,
        action.payload.defaultValues
      );
      case ACTIONS.UPDATE_GT:
      return updateGt(
        state,
        action.payload.componentsData,
      );
       case ACTIONS.UPDATE_GT_CB:
      return updateGtCb(state, action.payload.componentsData);
      case ACTIONS.SELECT_GT_CBASSET:
        return selectGtCbAsset(
          state,
          action.payload.index,
          action.payload.selectedAsset,
          action.payload.cbCommunicationValues
        );
      case ACTIONS.PREV_GT_DATA:
        return getPrevGtData(state, action.payload.gtData);
      default:
      return state;
  }
}
const getGtCbIp = cbCommunicationValues => {
  const ifeIp1 = CB_IFE_IP_ADDRESSES.IFE_IP_1;
  const ifeIp2 = CB_IFE_IP_ADDRESSES.IFE_IP_2;
  const ifeIp3 = CB_IFE_IP_ADDRESSES.IFE_IP_3;
  const ifeIp4 = CB_IFE_IP_ADDRESSES.IFE_IP_4;

  const ifeSet1 = cbCommunicationValues.ifeIpSets[ifeIp1]?.[0] ?? 0;
  const ifeSet2 = cbCommunicationValues.ifeIpSets[ifeIp2]?.[0] ?? 0;
  const ifeSet3 = cbCommunicationValues.ifeIpSets[ifeIp3]?.[0] ?? 0;
  const ifeSet4 = cbCommunicationValues.ifeIpSets[ifeIp4]?.[0] ?? 0;

  const ifeSet1Max = cbCommunicationValues.ifeIpSets[ifeIp1]?.[1] ?? 0;
  const ifeSet2Max = cbCommunicationValues.ifeIpSets[ifeIp2]?.[1] ?? 0;
  const ifeSet3Max = cbCommunicationValues.ifeIpSets[ifeIp3]?.[1] ?? 0;
  const ifeSet4Max = cbCommunicationValues.ifeIpSets[ifeIp4]?.[1] ?? 0;

  const ipaddress =
    ifeSet1 !== ifeSet1Max
      ? ifeIp1
      : ifeSet2 !== ifeSet2Max
        ? ifeIp2
        : ifeSet3 !== ifeSet3Max
          ? ifeIp3
          : ifeSet4 !== ifeSet4Max
            ? ifeIp4
            : cbCommunicationValues.availableIps[0];
  return ipaddress;
};

const getCBifeValues = (
  gtDataPrevious,
  selectedAsset,
  cbCommunicationValues
) => {
 
  const numberOfDevicesIFE = parseInt(
    selectedAsset?.assetAttributes?.find(
      assetAttribute => assetAttribute.type === "numberOfDevicesIFE"
    )?.value ?? 7
  );
 
  if (numberOfDevicesIFE === 1) {
    const ipaddress = cbCommunicationValues.availableIps[0];
    return {
      ...gtDataPrevious.cbCommunicationData,
      ipaddress: ipaddress,
      modbusSlaveId: "255",
    };
  }

  if (numberOfDevicesIFE === 7) {
    
    return {
      ...gtDataPrevious.cbCommunicationData,
      ipaddress: getGtCbIp(cbCommunicationValues),
      modbusSlaveId: cbCommunicationValues.ifeIpSets[
        getGtCbIp(cbCommunicationValues)
      ]
        ? gtDataPrevious.cbCommunicationData.modbusSlaveId
        : "255",
    };
  }
};
export { initialCBGTList, CBGTListReducer };